import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MessageCircle, User, ShoppingBag, Store } from 'lucide-react'
import type { LojaInfo } from './lojaApi'
import { getLojaToken } from './lojaApi'
import { useCarrinho } from './useCarrinho'

interface Props {
  info: LojaInfo | null
  slug: string
  children: React.ReactNode
}

export default function LojaLayout({ info, slug, children }: Props) {
  const navigate = useNavigate()
  const totalItens = useCarrinho((s) => s.getTotalItens(slug))
  const isLogged = !!getLojaToken()

  useEffect(() => {
    if (info?.nome) document.title = `${info.nome} · Loja`
    return () => { document.title = 'VELORE ERP' }
  }, [info?.nome])

  return (
    <div className="min-h-screen bg-ink-50 text-ink-900 flex flex-col">
      {/* HEADER */}
      <header className="bg-white border-b border-ink-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Link to={`/loja/${slug}`} className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-md bg-brand-600 text-white flex items-center justify-center shrink-0">
              {info?.logo_url
                ? <img src={info.logo_url} alt="" className="w-full h-full object-cover rounded-md" />
                : <Store size={18} />}
            </div>
            <div className="min-w-0 hidden sm:block">
              <div className="font-semibold text-ink-900 leading-tight truncate">
                {info?.nome || 'Carregando...'}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-ink-500">Loja online</div>
            </div>
          </Link>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            {info?.whatsapp && (
              <a
                href={`https://wa.me/55${info.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-sm hidden sm:inline-flex"
                title="Falar no WhatsApp"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}

            <button
              type="button"
              onClick={() => navigate(isLogged ? `/loja/${slug}/minha-conta` : `/loja/${slug}/login`)}
              className="btn-ghost text-sm"
            >
              <User size={16} />
              <span className="hidden sm:inline">{isLogged ? 'Minha conta' : 'Entrar'}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate(`/loja/${slug}/carrinho`)}
              className="btn-primary text-sm relative"
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">Carrinho</span>
              {totalItens > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {totalItens > 99 ? '99+' : totalItens}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* CONTEUDO */}
      <main className="flex-1">{children}</main>

      {/* FOOTER */}
      <footer className="bg-white border-t border-ink-200 mt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 text-sm text-ink-500 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div>
            <div className="font-semibold text-ink-900 mb-1">{info?.nome}</div>
            {info?.endereco && (
              <div className="text-xs">
                {info.endereco.logradouro}, {info.endereco.numero}
                {info.endereco.bairro && ` · ${info.endereco.bairro}`}
                {info.endereco.cidade && ` · ${info.endereco.cidade}/${info.endereco.uf}`}
              </div>
            )}
          </div>
          <div className="text-xs">Loja online do VELORE ERP</div>
        </div>
      </footer>
    </div>
  )
}
